import { Link } from "react-router-dom";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { HardHat, Check, Truck, MapPin, Gauge, FileSignature } from "lucide-react";
import { SEO } from "@/lib/seo";
import { PublicFooter } from "./FAQ";
import { RelatedPages } from "@/components/RelatedPages";

// Keyword targets: "fleet management NZ", "fleet management app New Zealand",
// "ute tracking NZ", "construction fleet software NZ", "RUC monitoring NZ",
// "Cartrack integration NZ"
export default function FleetManagementNZ() {
  const faqs = [
    {
      q: "Do I need Cartrack to use Fieldlink Fleet?",
      a: "No. The core fleet module (assets, pre-starts, damage reports, WoF / RUC / CoF dates) works without any telematics provider. If you already have Cartrack, plug it in and you also get live vehicle positions on a map, real-time odometers and trip history.",
    },
    {
      q: "Which telematics providers does Fieldlink connect to?",
      a: "Cartrack today (live tracking, trips, geofences, events). EROAD, Coretex, Teletrac Navman and Inseego are on the roadmap — let us know what you use and we'll prioritise.",
    },
    {
      q: "How does Fieldlink track RUC?",
      a: "You record each RUC purchase against a vehicle. Fieldlink pulls the live odometer from Cartrack and shows remaining km in real time, with red/amber alerts when a vehicle's running low. One-tap deep-link to NZTA's portal pre-fills the rego and km when it's time to buy more.",
    },
    {
      q: "Can field workers do pre-starts on their phone?",
      a: "Yes — operators select the machine and complete its company checklist with photos and a signature. Designated mechanical failures create damage reports; an overall unsafe response sets the machine to maintenance pending office review.",
    },
    {
      q: "Where's the data hosted?",
      a: "MongoDB Atlas in the Sydney (ap-southeast-2) region — the closest cloud region to NZ. Encrypted at rest, isolated per organisation (multi-tenant walled garden).",
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <div className="web-seo bg-[#f6f5f1] text-[#142635] min-h-screen">
      <SEO
        title="Fleet Management NZ — Live Tracking, RUC Monitoring, Pre-Starts | Fieldlink"
        description="NZ-made fleet management for civil construction. Live vehicle tracking via Cartrack, RUC monitoring with NZTA deep-link, daily pre-starts, damage reports, WoF/CoF dates. 7-day free trial."
        path="/fleet-management-nz"
        jsonLd={jsonLd}
      />

      <Header />

      <section className="bg-[#f6f5f1] text-[#142635] border-b border-[#cbd3d5] py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="block text-[11px] font-mono font-bold uppercase tracking-[0.28em] text-[#a63c0b] mb-4">Live tracking + RUC monitoring</div>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none mb-5">
            Fleet Management<br /><span className="text-[#a63c0b]">For NZ Civil Crews</span>
          </h1>
          <p className="text-[#465562] text-base sm:text-lg max-w-2xl">
            Live positions on a map, pre-starts on the phone, damage reports straight to the office, and RUC remaining km that updates as the truck drives. Plugs into Cartrack — no telemetry-provider lock-in.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" className="rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-6 py-3" data-testid="fleet-cta">Start free trial</Link>
            <Link to="/pricing" className="rounded-md border border-[#cbd3d5] bg-white hover:bg-white text-[#142635] font-bold uppercase tracking-wider px-6 py-3">See pricing</Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-3">Everything in one place</h2>
        <p className="text-[#52616c] max-w-2xl mb-10">
          Replace the patchwork of Excel sheets, the Cartrack dashboard tab no-one looks at, and the WhatsApp messages about flat tyres.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card icon={MapPin} title="Live map of every vehicle" body="OpenStreetMap pins, rego labels, orange = moving, grey = stopped. Polls every 15 seconds via Cartrack." />
          <Card icon={Gauge} title="RUC remaining km" body="Records each RUC purchase + pulls live odometer. Red badge when low, one-tap to NZTA's portal pre-filled with the rego and km." />
          <Card icon={Truck} title="Daily pre-starts" body="Machine-linked checks, photos and signatures. An overall unsafe response sets maintenance status for review." />
          <Card icon={FileSignature} title="Damage + incident reports" body="Workers record defects with photos against the machine, ready for office and workshop review." />
          <Card icon={Check} title="WoF / CoF / RUC dates" body="One screen showing every expiry across the fleet. Auto-emails to office when something falls due." />
          <Card icon={Check} title="Plant servicing" body="Service intervals by hours or km. Next service due, last service done, parts and supplier history." />
        </div>
      </section>

      <section className="bg-[#e9eeec] border-y border-[#cbd3d5] py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-3">How Cartrack + Fieldlink fit together</h2>
          <p className="text-[#52616c] max-w-2xl mb-8">
            Already on Cartrack? Paste your Fleet API credentials into Fieldlink (Settings → Live Tracking → Connect Cartrack) and your existing fleet shows up — including live positions, trips, geofences and events.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              "All Cartrack vehicles appear in Fieldlink automatically",
              "Live positions polled every 15s",
              "Trip history searchable by date + rego",
              "Geofences and events mirrored across",
              "Office-only view — field workers don't see telematics",
              "Credentials stored encrypted-at-rest (Fernet AES)",
            ].map((line) => (
              <div key={line} className="flex items-start gap-2 rounded-md bg-white border border-[#cbd3d5] p-3">
                <Check className="w-4 h-4 text-[#a63c0b] mt-0.5 shrink-0" />
                <span className="text-sm">{line}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-8">Fleet management FAQs</h2>
        <div className="space-y-4">
          {faqs.map((f) => (
            <details key={f.q} className="rounded-lg border border-[#cbd3d5] bg-white p-4 group">
              <summary className="font-bold cursor-pointer list-none flex justify-between items-start">
                <span>{f.q}</span>
                <span className="text-[#a63c0b] ml-3 group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-[#465562] text-sm leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="bg-[#e9eeec] border-y border-[#cbd3d5] text-[#142635] py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mb-4">Get your fleet on one page</h2>
          <p className="text-[#52616c] mb-8">7-day free trial, plug-and-play with Cartrack.</p>
          <Link to="/register" className="inline-block rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-8 py-4">Start free trial</Link>
          <div className="mt-6 text-xs text-[#52616c]">
            <Link to="/civil-construction-software-nz" className="underline hover:text-[#142635]">Civil construction software</Link> ·{" "}
            <Link to="/sssp-generator-nz" className="underline hover:text-[#142635]">SSSP generator</Link> ·{" "}
            <Link to="/safety-management-nz" className="underline hover:text-[#142635]">Safety management</Link>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-12 py-10"><p className="text-[#465562]">Planning an excavator or truck move? Review the <Link to="/civil-crew-scheduling-software-nz" className="underline underline-offset-4">crew and plant allocations for the job</Link> alongside the equipment record, then confirm the site arrangements with the team.</p></section>
      <section className="max-w-4xl mx-auto px-6 py-8"><p>For the operator-to-office workflow, see <Link to="/pre-start-checklist-app-nz" className="underline">plant pre-start records</Link>. Keep site forms and follow-up connected through the <Link to="/safety-management-nz" className="underline">Health &amp; Safety workspace</Link>.</p></section>
      <RelatedPages current="/fleet-management-nz" />

      <PublicFooter />
    </div>
  );
}

function Header() {
  return (
      <MarketingHeader />
  );
}

function Card({ icon: Icon, title, body }) {
  return (
    <div className="rounded-lg border border-[#cbd3d5] bg-white p-6">
      <Icon className="w-7 h-7 text-[#a63c0b] mb-4" />
      <h3 className="font-display font-extrabold uppercase tracking-tight text-xl mb-2">{title}</h3>
      <p className="text-[#52616c] text-sm leading-relaxed">{body}</p>
    </div>
  );
}
